import React, { useState } from 'react';
import { getSystemAssets, updateSystemAssets } from '../utils/systemAssets';
import { API_BASE_URL } from '../config/api';

function AdminBusinessPanel({ user, onLogout, language = 'english', setLanguage = () => {} }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [systemAssets, setSystemAssets] = useState(getSystemAssets());
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);

  // Fetch businesses from API
  React.useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/parent-businesses');
      const data = await response.json();
      // Convert API data to component format
      const formattedBusinesses = data.map(b => ({
        id: b._id,
        name: b.businessName,
        owner: b.parentName,
        category: b.businessCategory.toLowerCase(),
        description: b.businessDescription,
        image: `${API_BASE_URL}${b.businessPhoto}`,
        rating: b.rating || 0,
        reviews: b.reviewCount || 0,
        price: b.pricing?.priceRange || 'N/A',
        phone: b.contactNumber,
        whatsapp: b.whatsappNumber,
        address: b.businessAddress,
        verified: b.isVerified,
        studentInfo: `Parent of ${b.studentName} - Class ${b.studentClass}`,
        gallery: [b.businessPhoto],
        status: b.isActive ? 'active' : 'inactive'
      }));
      setBusinesses(formattedBusinesses);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    english: {
      title: 'Business Manager Admin Panel',
      dashboard: 'Dashboard',
      businesses: 'Manage Businesses',
      users: 'Manage Users',
      reports: 'Reports',
      settings: 'Settings'
    },
    hindi: {
      title: 'बिजनेस मैनेजर एडमिन पैनल',
      dashboard: 'डैशबोर्ड',
      businesses: 'व्यवसाय प्रबंधन',
      users: 'यूजर प्रबंधन',
      reports: 'रिपोर्ट',
      settings: 'सेटिंग्स'
    }
  };

  const t = translations[language] || translations.english;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F8F9FA',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      {/* Admin Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1E5AA8 0%, #F57C00 100%)',
        color: 'white',
        padding: '16px 20px',
        boxShadow: '0 2px 12px rgba(30, 90, 168, 0.15)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundImage: `url(${systemAssets.risingLogo})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: '8px'
            }}></div>
            <div>
              <h1 style={{
                fontSize: '18px',
                fontWeight: '600',
                margin: 0
              }}>{t.title}</h1>
              <p style={{
                fontSize: '12px',
                opacity: '0.9',
                margin: 0
              }}>Admin Level 2 - Business Manager</p>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Language Toggle */}
            <div style={{
              display: 'flex',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '6px',
              padding: '2px'
            }}>
              <button
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: language === 'english' ? 'white' : 'transparent',
                  color: language === 'english' ? '#2D3748' : 'rgba(255,255,255,0.8)',
                  fontWeight: '500'
                }}
                onClick={() => setLanguage('english')}
              >
                EN
              </button>
              <button
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: language === 'hindi' ? 'white' : 'transparent',
                  color: language === 'hindi' ? '#2D3748' : 'rgba(255,255,255,0.8)',
                  fontWeight: '500'
                }}
                onClick={() => setLanguage('hindi')}
              >
                हिं
              </button>
            </div>
            
            <button 
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
              onClick={onLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Admin Navigation */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #E2E8F0',
        padding: '0 20px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          gap: '0'
        }}>
          {[
            { id: 'dashboard', label: t.dashboard, icon: '📊' },
            { id: 'businesses', label: t.businesses, icon: '🏪' },
            { id: 'users', label: t.users, icon: '👥' },
            { id: 'reports', label: t.reports, icon: '📈' },
            { id: 'settings', label: t.settings, icon: '⚙️' }
          ].map(tab => (
            <button
              key={tab.id}
              style={{
                background: 'none',
                border: 'none',
                padding: '16px 20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === tab.id ? '#1E5AA8' : '#6B7280',
                borderBottom: activeTab === tab.id ? '2px solid #1E5AA8' : '2px solid transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Admin Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
      }}>
        {activeTab === 'dashboard' && (
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#2D3748',
              marginBottom: '20px'
            }}>{language === 'english' ? 'Business Community Overview' : 'बिजनेस कम्युनिटी अवलोकन'}</h2>
            
            {/* Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#ECFDF5',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>🏪</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#10B981'
                    }}>{businesses.length}</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Total Businesses' : 'कुल व्यवसाय'}</div>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#EFF6FF',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>👥</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#3B82F6'
                    }}>1,234</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Active Parents' : 'सक्रिय अभिभावक'}</div>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#FFFBEB',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>⚠️</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#F59E0B'
                    }}>{businesses.filter(b => !b.verified).length}</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Pending Approvals' : 'लंबित अनुमोदन'}</div>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#FEF2F2',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>📈</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#EF4444'
                    }}>89</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Reports Today' : 'आज की रिपोर्ट'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #E2E8F0'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#2D3748',
                marginBottom: '16px'
              }}>{language === 'english' ? 'Quick Actions' : 'त्वरित कार्य'}</h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                <button style={{
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => setActiveTab('businesses')}
                >
                  ✓ {language === 'english' ? 'Approve Businesses' : 'व्यवसाय अनुमोदित करें'}
                </button>
                
                <button style={{
                  background: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => setActiveTab('users')}
                >
                  📝 {language === 'english' ? 'Manage Users' : 'यूजर प्रबंधन'}
                </button>
                
                <button style={{
                  background: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => setActiveTab('reports')}
                >
                  📈 {language === 'english' ? 'View Reports' : 'रिपोर्ट देखें'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'businesses' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#2D3748',
                margin: 0
              }}>{language === 'english' ? 'Business Management' : 'व्यवसाय प्रबंधन'}</h2>
              
              <button
                style={{
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => setShowCreateForm(true)}
              >
                + {language === 'english' ? 'Create New Business' : 'नया व्यवसाय बनाएं'}
              </button>
            </div>
            
            {/* Business List */}
            <div style={{
              display: 'grid',
              gap: '16px'
            }}>
              {businesses.map(business => (
                <div key={business.id} style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '1px solid #E2E8F0'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '16px'
                  }}>
                    <img
                      src={business.image}
                      alt={business.name}
                      style={{
                        width: '100px',
                        height: '80px',
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#1E5AA8',
                          margin: 0
                        }}>{business.name}</h3>
                        <div style={{
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <span style={{
                            background: business.verified ? '#ECFDF5' : '#FEF3C7',
                            color: business.verified ? '#059669' : '#D97706',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {business.verified ? '✓ Verified' : '⚠ Pending'}
                          </span>
                          <span style={{
                            background: business.status === 'active' ? '#ECFDF5' : '#FEF2F2',
                            color: business.status === 'active' ? '#059669' : '#DC2626',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {business.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <p style={{
                        fontSize: '14px',
                        color: '#475569',
                        margin: '0 0 8px 0'
                      }}>by {business.owner} • {business.studentInfo}</p>
                      <p style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        margin: '0 0 12px 0',
                        lineHeight: '1.4'
                      }}>{business.description}</p>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '16px',
                          fontSize: '12px',
                          color: '#6B7280'
                        }}>
                          <span>⭐ {business.rating} ({business.reviews} reviews)</span>
                          <span>📞 {business.phone}</span>
                          <span>📍 {business.address}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <button
                            style={{
                              background: '#3B82F6',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            onClick={() => setSelectedBusiness(business)}
                          >
                            {language === 'english' ? 'Edit' : 'संपादित करें'}
                          </button>
                          <button
                            style={{
                              background: business.status === 'active' ? '#EF4444' : '#10B981',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            onClick={() => {
                              setBusinesses(prev => prev.map(b => 
                                b.id === business.id 
                                  ? { ...b, status: b.status === 'active' ? 'inactive' : 'active' }
                                  : b
                              ));
                            }}
                          >
                            {business.status === 'active' 
                              ? (language === 'english' ? 'Deactivate' : 'निष्क्रिय करें')
                              : (language === 'english' ? 'Activate' : 'सक्रिय करें')
                            }
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#2D3748',
                margin: 0
              }}>{language === 'english' ? 'User Management' : 'यूजर प्रबंधन'}</h2>
              
              <button
                style={{
                  background: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => setShowAddUserForm(true)}
              >
                + {language === 'english' ? 'Add New User' : 'नया यूजर जोड़ें'}
              </button>
            </div>
            
            {/* User Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#EFF6FF',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>👥</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#3B82F6'
                    }}>{businesses.length}</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Total Parents' : 'कुल अभिभावक'}</div>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#ECFDF5',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>✓</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#10B981'
                    }}>{businesses.filter(b => b.verified).length}</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Verified Users' : 'सत्यापित यूजर'}</div>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#FFFBEB',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>⏳</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#F59E0B'
                    }}>{businesses.filter(b => !b.verified).length}</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Pending Verification' : 'सत्यापन लंबित'}</div>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#FEF2F2',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>🏪</div>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#EF4444'
                    }}>{businesses.filter(b => b.status === 'active').length}</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>{language === 'english' ? 'Active Businesses' : 'सक्रिय व्यवसाय'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* User List */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #E2E8F0',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #E2E8F0',
                background: '#F8FAFC'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#2D3748',
                  margin: 0
                }}>{language === 'english' ? 'Parent Users with Businesses' : 'व्यवसाय वाले अभिभावक यूजर'}</h3>
              </div>
              
              <div style={{ padding: '0' }}>
                {businesses.map((business, index) => (
                  <div key={index} style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #F1F5F9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flex: 1
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: business.verified ? '#10B981' : '#F59E0B',
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        {business.owner.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2D3748',
                          marginBottom: '2px'
                        }}>{business.owner}</div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          marginBottom: '2px'
                        }}>{business.studentInfo}</div>
                        <div style={{
                          fontSize: '11px',
                          color: '#9CA3AF'
                        }}>Business: {business.name}</div>
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        background: business.verified ? '#ECFDF5' : '#FFFBEB',
                        color: business.verified ? '#059669' : '#D97706',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        {business.verified ? '✓ Verified' : '⏳ Pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#2D3748',
              marginBottom: '20px'
            }}>{language === 'english' ? 'Reports & Analytics' : 'रिपोर्ट और विश्लेषण'}</h2>
            
            {/* Analytics Overview */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2D3748',
                    margin: 0
                  }}>{language === 'english' ? 'Business Growth' : 'व्यवसाय वृद्धि'}</h3>
                  <div style={{
                    background: '#ECFDF5',
                    color: '#059669',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>+12%</div>
                </div>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#10B981',
                  marginBottom: '8px'
                }}>247</div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>{language === 'english' ? 'Total Businesses This Month' : 'इस महीने कुल व्यवसाय'}</div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2D3748',
                    margin: 0
                  }}>{language === 'english' ? 'User Engagement' : 'उपयोगकर्ता सहभागिता'}</h3>
                  <div style={{
                    background: '#EFF6FF',
                    color: '#2563EB',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>+8%</div>
                </div>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#3B82F6',
                  marginBottom: '8px'
                }}>89%</div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>{language === 'english' ? 'Active User Rate' : 'सक्रिय उपयोगकर्ता दर'}</div>
              </div>

              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2D3748',
                    margin: 0
                  }}>{language === 'english' ? 'Revenue Impact' : 'राजस्व प्रभाव'}</h3>
                  <div style={{
                    background: '#FFFBEB',
                    color: '#D97706',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>+25%</div>
                </div>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#F59E0B',
                  marginBottom: '8px'
                }}>₹2.4L</div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>{language === 'english' ? 'Monthly Community Revenue' : 'मासिक समुदाय राजस्व'}</div>
              </div>
            </div>

            {/* Category Performance */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #E2E8F0',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#2D3748',
                marginBottom: '16px'
              }}>{language === 'english' ? 'Business Category Performance' : 'व्यवसाय श्रेणी प्रदर्शन'}</h3>
              
              <div style={{
                display: 'grid',
                gap: '12px'
              }}>
                {[
                  { category: 'Food & Catering', count: 45, percentage: 85, color: '#10B981' },
                  { category: 'Education', count: 38, percentage: 78, color: '#3B82F6' },
                  { category: 'Beauty & Salon', count: 32, percentage: 65, color: '#8B5CF6' },
                  { category: 'Tailoring', count: 28, percentage: 58, color: '#F59E0B' },
                  { category: 'Repair Services', count: 24, percentage: 48, color: '#EF4444' },
                  { category: 'Healthcare', count: 18, percentage: 35, color: '#06B6D4' }
                ].map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '12px',
                    background: '#F8FAFC',
                    borderRadius: '8px'
                  }}>
                    <div style={{
                      width: '120px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#2D3748'
                    }}>{item.category}</div>
                    
                    <div style={{
                      flex: 1,
                      height: '8px',
                      background: '#E5E7EB',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${item.percentage}%`,
                        height: '100%',
                        background: item.color,
                        borderRadius: '4px'
                      }}></div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      minWidth: '80px'
                    }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2D3748'
                      }}>{item.count}</span>
                      <span style={{
                        fontSize: '12px',
                        color: '#6B7280'
                      }}>{item.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity & Trends */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              {/* Activity Log */}
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#2D3748',
                  marginBottom: '16px'
                }}>{language === 'english' ? 'Recent Activity' : 'हाल की गतिविधि'}</h3>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  {[
                    { action: 'New business registered', user: 'Priya Sharma', time: '2 hours ago', type: 'success' },
                    { action: 'User verification completed', user: 'Raj Kumar', time: '4 hours ago', type: 'info' },
                    { action: 'Business reported', user: 'Anonymous', time: '6 hours ago', type: 'warning' },
                    { action: 'Payment processed', user: 'Meera Patel', time: '8 hours ago', type: 'success' },
                    { action: 'Account suspended', user: 'Mohan Lal', time: '1 day ago', type: 'error' }
                  ].map((activity, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: '#F8FAFC',
                      borderRadius: '8px'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: activity.type === 'success' ? '#10B981' : 
                                   activity.type === 'info' ? '#3B82F6' :
                                   activity.type === 'warning' ? '#F59E0B' : '#EF4444'
                      }}></div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#2D3748',
                          marginBottom: '2px'
                        }}>{activity.action}</div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6B7280'
                        }}>{activity.user} • {activity.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Performers */}
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#2D3748',
                  marginBottom: '16px'
                }}>{language === 'english' ? 'Top Performing Businesses' : 'शीर्ष प्रदर्शन व्यवसाय'}</h3>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  {[
                    { name: 'Priya\'s Home Tutoring', rating: 4.8, reviews: 24, revenue: '₹45K' },
                    { name: 'Ayurveda Health Center', rating: 4.8, reviews: 35, revenue: '₹38K' },
                    { name: 'Raj\'s Tiffin Service', rating: 4.7, reviews: 32, revenue: '₹32K' },
                    { name: 'Meera\'s Tailoring Studio', rating: 4.6, reviews: 18, revenue: '₹28K' },
                    { name: 'Green Grocery Store', rating: 4.6, reviews: 41, revenue: '₹25K' }
                  ].map((business, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      background: '#F8FAFC',
                      borderRadius: '8px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#2D3748',
                          marginBottom: '2px'
                        }}>{business.name}</div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6B7280'
                        }}>⭐ {business.rating} ({business.reviews} reviews)</div>
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#10B981'
                      }}>{business.revenue}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#2D3748',
              marginBottom: '20px'
            }}>{language === 'english' ? 'System Settings' : 'सिस्टम सेटिंग्स'}</h2>
            
            {/* Settings Categories */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px',
              marginBottom: '24px'
            }}>
              {/* Business Policies */}
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#EFF6FF',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>🏪</div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2D3748',
                    margin: 0
                  }}>{language === 'english' ? 'Business Policies' : 'व्यावसायिक नीतियां'}</h3>
                </div>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#4A5568'
                    }}>{language === 'english' ? 'Auto-approve verified parents' : 'सत्यापित अभिभावकों को स्वतः अनुमोदित करें'}</span>
                    <div style={{
                      width: '40px',
                      height: '20px',
                      background: '#10B981',
                      borderRadius: '10px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        transition: 'all 0.2s ease'
                      }}></div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#4A5568'
                    }}>{language === 'english' ? 'Require photo verification' : 'फोटो सत्यापन आवश्यक'}</span>
                    <div style={{
                      width: '40px',
                      height: '20px',
                      background: '#10B981',
                      borderRadius: '10px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        right: '2px'
                      }}></div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#4A5568'
                    }}>{language === 'english' ? 'Allow multiple businesses per parent' : 'प्रति अभिभावक कई व्यवसायों की अनुमति दें'}</span>
                    <div style={{
                      width: '40px',
                      height: '20px',
                      background: '#E5E7EB',
                      borderRadius: '10px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: '2px'
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#FEF3C7',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>🔔</div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2D3748',
                    margin: 0
                  }}>{language === 'english' ? 'Notifications' : 'सूचनाएं'}</h3>
                </div>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#4A5568'
                    }}>{language === 'english' ? 'Email notifications' : 'ईमेल सूचनाएं'}</span>
                    <div style={{
                      width: '40px',
                      height: '20px',
                      background: '#10B981',
                      borderRadius: '10px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        right: '2px'
                      }}></div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#4A5568'
                    }}>{language === 'english' ? 'SMS notifications' : 'SMS सूचनाएं'}</span>
                    <div style={{
                      width: '40px',
                      height: '20px',
                      background: '#E5E7EB',
                      borderRadius: '10px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: '2px'
                      }}></div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#4A5568'
                    }}>{language === 'english' ? 'Push notifications' : 'पुश सूचनाएं'}</span>
                    <div style={{
                      width: '40px',
                      height: '20px',
                      background: '#10B981',
                      borderRadius: '10px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        right: '2px'
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Assets */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #E2E8F0',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#2D3748',
                marginBottom: '16px'
              }}>{language === 'english' ? 'System Assets' : 'सिस्टम एसेट्स'}</h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                {/* Rising360 Logo */}
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '12px'
                  }}>{language === 'english' ? 'Rising360 Logo' : 'Rising360 लोगो'}</h4>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '50px',
                      backgroundImage: `url(${systemAssets.risingLogo})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      border: '2px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#F9FAFB'
                    }}></div>
                    <div style={{ flex: 1 }}>
                      <p style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        margin: '0 0 8px 0'
                      }}>{language === 'english' ? 'Current logo displayed in header and parent dashboard' : 'हेडर और पैरेंट डैशबोर्ड में दिखाया गया वर्तमान लोगो'}</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setSystemAssets(prev => ({ ...prev, risingLogo: event.target.result }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        style={{ display: 'none' }}
                        id="rising-logo-upload"
                      />
                      <label
                        htmlFor="rising-logo-upload"
                        style={{
                          background: '#3B82F6',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          display: 'inline-block'
                        }}
                      >
                        📷 {language === 'english' ? 'Upload New Logo' : 'नया लोगो अपलोड करें'}
                      </label>
                    </div>
                  </div>
                  
                  <div style={{
                    fontSize: '11px',
                    color: '#9CA3AF',
                    background: '#F3F4F6',
                    padding: '8px',
                    borderRadius: '4px'
                  }}>
                    {language === 'english' 
                      ? 'Recommended: 200x100px, PNG/JPG, max 100KB'
                      : 'अनुशंसित: 200x100px, PNG/JPG, अधिकतम 100KB'
                    }
                  </div>
                </div>

                {/* Default Parent Image */}
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '12px'
                  }}>{language === 'english' ? 'Default Parent Image' : 'डिफ़ॉल्ट पैरेंट इमेज'}</h4>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      backgroundImage: `url(${systemAssets.parentImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      border: '2px solid #E5E7EB',
                      borderRadius: '50%',
                      backgroundColor: '#F9FAFB'
                    }}></div>
                    <div style={{ flex: 1 }}>
                      <p style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        margin: '0 0 8px 0'
                      }}>{language === 'english' ? 'Default profile image for new parent accounts' : 'नए पैरेंट अकाउंट के लिए डिफ़ॉल्ट प्रोफाइल इमेज'}</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setSystemAssets(prev => ({ ...prev, parentImage: event.target.result }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        style={{ display: 'none' }}
                        id="parent-image-upload"
                      />
                      <label
                        htmlFor="parent-image-upload"
                        style={{
                          background: '#10B981',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          display: 'inline-block'
                        }}
                      >
                        📷 {language === 'english' ? 'Upload New Image' : 'नई इमेज अपलोड करें'}
                      </label>
                    </div>
                  </div>
                  
                  <div style={{
                    fontSize: '11px',
                    color: '#9CA3AF',
                    background: '#F3F4F6',
                    padding: '8px',
                    borderRadius: '4px'
                  }}>
                    {language === 'english' 
                      ? 'Recommended: 150x150px, PNG/JPG, max 60KB'
                      : 'अनुशंसित: 150x150px, PNG/JPG, अधिकतम 60KB'
                    }
                  </div>
                </div>
              </div>
              
              {/* Save Assets Button */}
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <button
                  style={{
                    background: '#1E5AA8',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onClick={() => {
                    updateSystemAssets(systemAssets);
                    alert(language === 'english' 
                      ? 'System assets updated successfully! Changes will be visible across the platform.' 
                      : 'सिस्टम एसेट्स सफलतापूर्वक अपडेट किए गए! परिवर्तन पूरे प्लेटफॉर्म पर दिखाई देंगे।'
                    );
                  }}
                >
                  💾 {language === 'english' ? 'Save System Assets' : 'सिस्टम एसेट्स सहेजें'}
                </button>
              </div>
            </div>

            {/* System Configuration */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #E2E8F0',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#2D3748',
                marginBottom: '16px'
              }}>{language === 'english' ? 'System Configuration' : 'सिस्टम कॉन्फ़िगरेशन'}</h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>{language === 'english' ? 'Maximum businesses per parent' : 'प्रति अभिभावक अधिकतम व्यवसाय'}</label>
                  <select style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <option value="1">1 Business</option>
                    <option value="2" selected>2 Businesses</option>
                    <option value="3">3 Businesses</option>
                    <option value="unlimited">Unlimited</option>
                  </select>
                </div>
                
                <div>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>{language === 'english' ? 'Business approval method' : 'व्यवसाय अनुमोदन विधि'}</label>
                  <select style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <option value="manual" selected>Manual Review</option>
                    <option value="auto">Auto Approve</option>
                    <option value="hybrid">Hybrid (Auto + Manual)</option>
                  </select>
                </div>
                
                <div>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>{language === 'english' ? 'Community language' : 'समुदाय भाषा'}</label>
                  <select style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <option value="both" selected>English + Hindi</option>
                    <option value="english">English Only</option>
                    <option value="hindi">Hindi Only</option>
                  </select>
                </div>
                
                <div>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>{language === 'english' ? 'Photo upload limit' : 'फोटो अपलोड सीमा'}</label>
                  <select style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <option value="60">60 KB</option>
                    <option value="100" selected>100 KB</option>
                    <option value="200">200 KB</option>
                    <option value="500">500 KB</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Save Settings */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button style={{
                background: '#F3F4F6',
                color: '#374151',
                border: '1px solid #D1D5DB',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {language === 'english' ? 'Reset to Default' : 'डिफ़ॉल्ट पर रीसेट करें'}
              </button>
              <button style={{
                background: '#10B981',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {language === 'english' ? 'Save Settings' : 'सेटिंग्स सहेजें'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Business Modal */}
      {selectedBusiness && (
        <BusinessEditModal
          business={selectedBusiness}
          onClose={() => setSelectedBusiness(null)}
          onSave={(updatedBusiness) => {
            setBusinesses(prev => prev.map(b => 
              b.id === updatedBusiness.id ? updatedBusiness : b
            ));
            setSelectedBusiness(null);
          }}
          language={language}
        />
      )}

      {/* Create Business Modal */}
      {showCreateForm && (
        <CreateBusinessModal
          onClose={() => setShowCreateForm(false)}
          onSave={() => {
            setShowCreateForm(false);
            fetchBusinesses(); // Refresh the list
          }}
          language={language}
        />
      )}

      {/* Add User Modal */}
      {showAddUserForm && (
        <AddUserModal
          onClose={() => setShowAddUserForm(false)}
          onSave={() => {
            setShowAddUserForm(false);
            alert(language === 'english' ? 'Parent account created successfully!' : 'अभिभावक खाता सफलतापूर्वक बनाया गया!');
          }}
          language={language}
        />
      )}
    </div>
  );
}

// Business Edit Modal Component
function BusinessEditModal({ business, onClose, onSave, language }) {
  const [formData, setFormData] = useState({ ...business });

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('uploadType', 'business');
      
      try {
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formDataUpload
        });
        
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({ ...prev, image: data.path }));
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Failed to upload photo');
      }
    }
  };

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#2D3748',
            margin: 0
          }}>{language === 'english' ? 'Edit Business' : 'व्यवसाय संपादित करें'}</h3>
          <button
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6B7280'
            }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Photo Section */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px',
            display: 'block'
          }}>{language === 'english' ? 'Business Photo' : 'व्यवसाय फोटो'}</label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <img
              src={formData.image}
              alt={formData.name}
              style={{
                width: '100px',
                height: '80px',
                borderRadius: '8px',
                objectFit: 'cover',
                border: '2px solid #E5E7EB'
              }}
            />
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                style={{
                  background: '#3B82F6',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-block'
                }}
              >
                📷 {language === 'english' ? 'Change Photo' : 'फोटो बदलें'}
              </label>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div style={{
          display: 'grid',
          gap: '16px'
        }}>
          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Business Name' : 'व्यवसाय का नाम'}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Description' : 'विवरण'}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
                display: 'block'
              }}>{language === 'english' ? 'Phone' : 'फोन'}</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
                display: 'block'
              }}>{language === 'english' ? 'Price Range' : 'मूल्य सीमा'}</label>
              <input
                type="text"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Address' : 'पता'}</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.verified}
                onChange={(e) => setFormData(prev => ({ ...prev, verified: e.target.checked }))}
                style={{ cursor: 'pointer' }}
              />
              <span style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>{language === 'english' ? 'Verified Business' : 'सत्यापित व्यवसाय'}</span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '24px',
          justifyContent: 'flex-end'
        }}>
          <button
            style={{
              background: '#F3F4F6',
              color: '#374151',
              border: '1px solid #D1D5DB',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onClick={onClose}
          >
            {language === 'english' ? 'Cancel' : 'रद्द करें'}
          </button>
          <button
            style={{
              background: '#10B981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onClick={handleSave}
          >
            {language === 'english' ? 'Save Changes' : 'परिवर्तन सहेजें'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Create Business Modal Component
function CreateBusinessModal({ onClose, onSave, language }) {
  const [formData, setFormData] = useState({
    name: '',
    owner: '',
    parentEmail: '',
    parentPassword: '',
    category: 'education',
    description: '',
    image: 'https://via.placeholder.com/300x200/E5E7EB/9CA3AF?text=Business',
    rating: 0,
    reviews: 0,
    price: '',
    phone: '',
    whatsapp: '',
    address: '',
    verified: false,
    studentInfo: '',
    gallery: [],
    status: 'active'
  });

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('uploadType', 'business');
      
      try {
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formDataUpload
        });
        
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({ ...prev, image: data.path }));
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Failed to upload photo');
      }
    }
  };

  const handleSave = async () => {
    if (formData.name && formData.owner && formData.description) {
      try {
        const response = await fetch('/api/parent-businesses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parentEmail: formData.parentEmail,
            parentPassword: formData.parentPassword,
            parentId: '',
            parentName: formData.owner,
            studentName: formData.studentInfo ? formData.studentInfo.split(' - ')[0].replace('Parent of ', '') : formData.owner,
            studentClass: formData.studentInfo ? (formData.studentInfo.split(' - ')[1] || 'N/A') : 'N/A',
            businessName: formData.name,
            businessCategory: formData.category,
            businessDescription: formData.description,
            contactNumber: formData.phone,
            whatsappNumber: formData.whatsapp,
            businessAddress: formData.address,
            businessPhoto: formData.image,
            parentPhoto: formData.image,
            pricing: {
              basePrice: formData.price,
              priceRange: formData.price
            },
            isVerified: formData.verified,
            createdBy: 'admin'
          })
        });

        if (response.ok) {
          onSave(formData);
        } else {
          const error = await response.json();
          alert(error.error || (language === 'english' ? 'Failed to create business' : 'व्यवसाय बनाने में विफल'));
        }
      } catch (error) {
        console.error('Error creating business:', error);
        alert(language === 'english' ? 'Error creating business' : 'व्यवसाय बनाने में त्रुटि');
      }
    } else {
      alert(language === 'english' ? 'Please fill all required fields' : 'कृपया सभी आवश्यक फ़ील्ड भरें');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#2D3748',
            margin: 0
          }}>{language === 'english' ? 'Create New Business' : 'नया व्यवसाय बनाएं'}</h3>
          <button
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6B7280'
            }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Photo Section */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px',
            display: 'block'
          }}>{language === 'english' ? 'Business Photo' : 'व्यवसाय फोटो'}</label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <img
              src={formData.image}
              alt="Business"
              style={{
                width: '100px',
                height: '80px',
                borderRadius: '8px',
                objectFit: 'cover',
                border: '2px solid #E5E7EB'
              }}
            />
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="create-photo-upload"
              />
              <label
                htmlFor="create-photo-upload"
                style={{
                  background: '#3B82F6',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-block'
                }}
              >
                📷 {language === 'english' ? 'Upload Photo' : 'फोटो अपलोड करें'}
              </label>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div style={{
          display: 'grid',
          gap: '16px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
                display: 'block'
              }}>{language === 'english' ? 'Business Name *' : 'व्यवसाय का नाम *'}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder={language === 'english' ? 'Enter business name' : 'व्यवसाय का नाम दर्ज करें'}
              />
            </div>
            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
                display: 'block'
              }}>{language === 'english' ? 'Owner Name *' : 'मालिक का नाम *'}</label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder={language === 'english' ? 'Enter owner name' : 'मालिक का नाम दर्ज करें'}
              />
            </div>
          </div>

          <div style={{
            background: '#EFF6FF',
            border: '2px solid #BFDBFE',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '8px'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1E40AF',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>🔐 {language === 'english' ? 'Parent Login Credentials' : 'अभिभावक लॉगिन क्रेडेंशियल'}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                  display: 'block'
                }}>{language === 'english' ? 'Email (for login) *' : 'ईमेल (लॉगिन के लिए) *'}</label>
                <input
                  type="email"
                  value={formData.parentEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, parentEmail: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="parent@example.com"
                />
              </div>
              <div>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                  display: 'block'
                }}>{language === 'english' ? 'Password *' : 'पासवर्ड *'}</label>
                <input
                  type="password"
                  value={formData.parentPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, parentPassword: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter password"
                />
              </div>
            </div>
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              marginTop: '8px',
              marginBottom: 0,
              fontStyle: 'italic'
            }}>{language === 'english' ? 'Parent will use this email and password to login and manage their business' : 'अभिभावक इस ईमेल और पासवर्ड का उपयोग लॉगिन करने और अपने व्यवसाय को प्रबंधित करने के लिए करेंगे'}</p>
          </div>

          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Category' : 'श्रेणी'}</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="education">Education</option>
              <option value="food">Food & Catering</option>
              <option value="tailoring">Tailoring</option>
              <option value="beauty">Beauty & Salon</option>
              <option value="repair">Repair Services</option>
              <option value="healthcare">Healthcare</option>
            </select>
          </div>

          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Description *' : 'विवरण *'}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
              placeholder={language === 'english' ? 'Describe your business...' : 'अपने व्यवसाय का वर्णन करें...'}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
                display: 'block'
              }}>{language === 'english' ? 'Phone' : 'फोन'}</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value, whatsapp: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="1234567890"
              />
            </div>
            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
                display: 'block'
              }}>{language === 'english' ? 'Price Range' : 'मूल्य सीमा'}</label>
              <input
                type="text"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="₹500-1000"
              />
            </div>
          </div>

          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Address' : 'पता'}</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder={language === 'english' ? 'Business address' : 'व्यवसाय का पता'}
            />
          </div>

          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Student Info' : 'छात्र जानकारी'}</label>
            <input
              type="text"
              value={formData.studentInfo}
              onChange={(e) => setFormData(prev => ({ ...prev, studentInfo: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="Parent of [Student Name] - Class [X]A"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '24px',
          justifyContent: 'flex-end'
        }}>
          <button
            style={{
              background: '#F3F4F6',
              color: '#374151',
              border: '1px solid #D1D5DB',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onClick={onClose}
          >
            {language === 'english' ? 'Cancel' : 'रद्द करें'}
          </button>
          <button
            style={{
              background: '#10B981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onClick={handleSave}
          >
            {language === 'english' ? 'Create Business' : 'व्यवसाय बनाएं'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add User Modal Component
function AddUserModal({ onClose, onSave, language }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    parentId: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!formData.email || !formData.password || !formData.name || !formData.parentId) {
      alert(language === 'english' ? 'Please fill all fields' : 'कृपया सभी फ़ील्ड भरें');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/admin2/parents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to create parent account');
      }
    } catch (error) {
      alert('Error creating parent account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '100%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#2D3748',
            margin: 0
          }}>{language === 'english' ? 'Add New Parent User' : 'नया अभिभावक यूजर जोड़ें'}</h3>
          <button
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6B7280'
            }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Parent Name *' : 'अभिभावक का नाम *'}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="Enter parent name"
            />
          </div>

          <div>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
              display: 'block'
            }}>{language === 'english' ? 'Parent ID *' : 'अभिभावक ID *'}</label>
            <input
              type="text"
              value={formData.parentId}
              onChange={(e) => setFormData(prev => ({ ...prev, parentId: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="PARENT_JOHN_5A"
            />
          </div>

          <div style={{
            background: '#EFF6FF',
            border: '2px solid #BFDBFE',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1E40AF',
              marginBottom: '12px'
            }}>🔐 {language === 'english' ? 'Login Credentials' : 'लॉगिन क्रेडेंशियल'}</h4>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                  display: 'block'
                }}>{language === 'english' ? 'Email *' : 'ईमेल *'}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="parent@example.com"
                />
              </div>

              <div>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                  display: 'block'
                }}>{language === 'english' ? 'Password *' : 'पासवर्ड *'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter password"
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '24px',
          justifyContent: 'flex-end'
        }}>
          <button
            style={{
              background: '#F3F4F6',
              color: '#374151',
              border: '1px solid #D1D5DB',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onClick={onClose}
            disabled={loading}
          >
            {language === 'english' ? 'Cancel' : 'रद्द करें'}
          </button>
          <button
            style={{
              background: loading ? '#9CA3AF' : '#10B981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (language === 'english' ? 'Creating...' : 'बनाया जा रहा है...') : (language === 'english' ? 'Create User' : 'यूजर बनाएं')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminBusinessPanel;